import React, { useState, useMemo } from "react";
import ScamActivityIncidentCard from "./ScamActivityIncidentCard";
import "./css/ScamActivityMap.css";

const SORT_OPTIONS = [
  { value: "date", label: "Date (newest first)" },
  { value: "distance", label: "Distance (closest first)" },
  { value: "type", label: "Scam type (A–Z)" },
];

/**
 * List view for the Scam Activity Map.
 * Renders sortable, expandable incident cards.
 *
 * @param {Object[]} incidents   - Array of ScamIncidentDTO
 * @param {number}   totalCount  - Total matching (before pagination)
 * @param {string}   radiusMiles - Radius used in search
 * @param {Object}   dateRange   - {from, to}
 * @param {string}   resolvedAddress
 */
function ScamActivityListView({
  incidents,
  totalCount,
  radiusMiles,
  dateRange,
  resolvedAddress,
}) {
  const [expandedId, setExpandedId] = useState(null);
  const [sortBy, setSortBy] = useState("date");

  const sorted = useMemo(() => {
    if (!incidents) return [];
    const copy = [...incidents];
    switch (sortBy) {
      case "distance":
        return copy.sort((a, b) => a.distanceMiles - b.distanceMiles);
      case "type":
        return copy.sort((a, b) => a.scamType.localeCompare(b.scamType));
      default: // date
        return copy.sort(
          (a, b) => new Date(b.reportedAt) - new Date(a.reportedAt)
        );
    }
  }, [incidents, sortBy]);

  const toggle = (id) => setExpandedId((prev) => (prev === id ? null : id));

  if (!incidents || incidents.length === 0) {
    return (
      <div className="sam-list-empty">
        <span className="sam-list-empty__icon">🎉</span>
        <h3>No scams reported in this area</h3>
        <p>
          No reports were found within {radiusMiles} miles of your address for
          the selected date range. This is good news — try expanding the radius
          or date range to see nearby trends.
        </p>
      </div>
    );
  }

  return (
    <div className="sam-list">
      {/* ── Summary banner ── */}
      <div className="sam-list__summary">
        <span className="sam-list__count">
          {totalCount} scam{totalCount !== 1 ? "s" : ""} reported
        </span>
        <span className="sam-list__summary-detail">
          within {radiusMiles} mi of{" "}
          <strong>{resolvedAddress || "your address"}</strong>
          {dateRange?.from && dateRange?.to
            ? ` · ${dateRange.from} to ${dateRange.to}`
            : ""}
        </span>
      </div>

      {/* ── Sort controls ── */}
      <div className="sam-list__controls">
        <label htmlFor="sam-sort" className="sam-list__sort-label">
          Sort by:
        </label>
        <select
          id="sam-sort"
          className="sam-list__sort-select"
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
        >
          {SORT_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* ── Cards ── */}
      <div className="sam-list__cards">
        {sorted.map((incident) => (
          <ScamActivityIncidentCard
            key={incident.id}
            incident={incident}
            expanded={expandedId === incident.id}
            onToggle={() => toggle(incident.id)}
          />
        ))}
      </div>
    </div>
  );
}

export default ScamActivityListView;
